import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import CircularLoading from "../components/CircularLoading";
import BookingDataLoadError from "../components/BookingDataLoadError";
import { FONT_FAMILY, labelText, getImportDetentionStatus, openEmptyContainerReturnProofPrint } from "../common/sharedFunctions";
import AdminStatusBadge from "../components/AdminStatus";
import { ActionButton, EmptyState, PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";
import {
  EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS,
  getEmptyContainerReturnDisplayPhotos,
  getEmptyContainerReturnPhotoUrl
} from "common/src/other/emptyContainerReturnProofs";

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("lll") : value;
};

const normalizeStatus = (returnRecord = {}) => {
  const value = returnRecord.emptyReturnStatus || returnRecord.status || "";
  const normalized = value.toString().trim().toLowerCase();
  if (normalized === "accepted") return "Accepted";
  if (normalized === "rejected") return "Rejected";
  if (normalized === "pending") return "Pending";
  return value;
};

const getGpsValue = (gps = {}, key) => {
  if (!gps) return "";
  if (key === "lat") return gps.lat !== undefined ? gps.lat : gps.latitude;
  return gps.lng !== undefined ? gps.lng : gps.longitude;
};

const getArrivalGps = (returnRecord = {}) => returnRecord.emptyReturnArrivalGps || returnRecord.arrivalPictureProof || {};
const getCompletionGps = (returnRecord = {}) => returnRecord.emptyReturnCompletionGps || {};

const formatMeters = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const parsed = parseFloat(value);
  return isNaN(parsed) ? value : `${Math.round(parsed)} m`;
};

const yesNoNa = (value, yesNo, emptyValue) => {
  if (value === true) return yesNo(true);
  if (value === false) return yesNo(false);
  return emptyValue;
};

const getFileUrl = (file) => {
  if (!file) return "";
  if (typeof file === "string") return file;
  return file.url || file.imageUrl || file.downloadURL || "";
};

const getProofFileUrl = (returnRecord = {}, key) => getFileUrl(returnRecord.proofs && returnRecord.proofs[key]);

const getEirReceiptUrl = (returnRecord = {}) => (
  returnRecord.emptyReturnEirReceiptUrl ||
  returnRecord.eirReceiptUrl ||
  getProofFileUrl(returnRecord, "eirReceiptPhoto") ||
  ""
);

const hasGps = (returnRecord = {}) => {
  const gps = getArrivalGps(returnRecord);
  const lat = getGpsValue(gps, "lat");
  const lng = getGpsValue(gps, "lng");
  return lat !== "" && lat !== undefined && lat !== null && lng !== "" && lng !== undefined && lng !== null;
};

const getContainerNumbers = (booking, returnRecord) => {
  const numbers = [];
  if (returnRecord.containerNumber) numbers.push(returnRecord.containerNumber);
  if (booking.containerNumber) numbers.push(booking.containerNumber);
  const additional = Array.isArray(returnRecord.additionalContainerNumbers)
    ? returnRecord.additionalContainerNumbers
    : booking.additionalContainerNumbers;
  if (Array.isArray(additional)) {
    additional.forEach((item) => {
      if (item && item.toString().trim()) numbers.push(item);
    });
  }
  return Array.from(new Set(numbers)).join(", ");
};

const getAdditionalContainerNumbers = (booking, returnRecord) => {
  const additional = Array.isArray(returnRecord.additionalContainerNumbers)
    ? returnRecord.additionalContainerNumbers
    : booking.additionalContainerNumbers;
  return Array.isArray(additional) ? additional.filter(Boolean).join(", ") : "";
};

const canSeeBooking = (booking, role, uid) => {
  if (role === "admin") return true;
  if (role === "customer") return booking.customer === uid;
  if (role === "driver") return booking.driver === uid;
  if (role === "fleetadmin") return booking.fleetadmin === uid || booking.fleetadminId === uid;
  return false;
};

const imageCell = (url, proof, alt) => {
  if (!url) return "";
  const stampText = proof && proof.stampText && !proof.stampEmbedded ? proof.stampText : "";
  return (
    <Box style={{ position: "relative", display: "inline-block", width: "100%", maxWidth: 300, background: "#F7FBFF", borderRadius: 18, overflow: "hidden" }}>
      <img
        src={url}
        alt={alt}
        style={{ display: "block", width: "100%", height: 190, objectFit: "cover" }}
      />
      {stampText ? (
        <Box
          style={{
            position: "absolute",
            left: 8,
            bottom: 8,
            color: "#fff",
            fontWeight: "bold",
            fontSize: 11,
            lineHeight: 1.25,
            whiteSpace: "pre-line",
            textShadow: "1px 1px 3px #000",
            background: "transparent"
          }}
        >
          {stampText}
        </Box>
      ) : null}
    </Box>
  );
};

export default function EmptyContainerReturns() {
  const { t } = useTranslation();
  const label = (key, fallback) => labelText(t, key, fallback);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const auth = useSelector((state) => state.auth);
  const role = auth.profile && auth.profile.usertype;
  const uid = auth.profile && auth.profile.uid;
  const isAdmin = role === "admin";
  const [selectedRow, setSelectedRow] = useState(null);

  const rows = useMemo(() => {
    const bookings = bookinglistdata.bookings || [];
    return bookings
      .filter((booking) => canSeeBooking(booking, role, uid))
      .flatMap((booking) => {
        const returns = booking.emptyContainerReturns ? Object.values(booking.emptyContainerReturns) : [];
        return returns.map((returnRecord) => {
          const proof = returnRecord.arrivalPictureProof || {};
          const arrivalGps = getArrivalGps(returnRecord);
          const completionGps = getCompletionGps(returnRecord);
          const status = normalizeStatus(returnRecord);
          const arrivalPhotoUrl = getEmptyContainerReturnPhotoUrl(returnRecord, "arrivalPhoto");
          const eirReceiptUrl = getEirReceiptUrl(returnRecord);
          const photos = getEmptyContainerReturnDisplayPhotos(returnRecord);
          const rejectionReason = returnRecord.emptyReturnRejectedReason || returnRecord.rejectionReason || "";
          const requiredPhotos = photos.filter((item) => item.required);
          const uploadedRequiredPhotoCount = requiredPhotos.filter((item) => item.url).length;
          const detentionInfo = getImportDetentionStatus({
            booking,
            importBookingInfo: booking.importBookingInfo,
            returnRecord
          });
          return {
            id: `${booking.id || booking.key || ""}-${returnRecord.id || returnRecord.createdAt || ""}`,
            bookingId: booking.id || booking.key || returnRecord.bookingId,
            bookingReference: returnRecord.bookingReference || booking.reference || "",
            driverName: returnRecord.driverName || booking.driver_name || "",
            customerName: returnRecord.customerName || booking.customer_name || "",
            consigneeName: returnRecord.consigneeName || (booking.importBookingInfo && booking.importBookingInfo.consigneeName) || booking.consigneeName || "",
            fleetAdminName: returnRecord.fleetAdminName || booking.fleetadminName || booking.fleetadmin || "",
            containerNumber: getContainerNumbers(booking, returnRecord),
            additionalContainerNumbers: getAdditionalContainerNumbers(booking, returnRecord),
            shippingLine: returnRecord.shippingLine || (booking.importBookingInfo && booking.importBookingInfo.shippingLine) || booking.shippingLine || "",
            containerYardName: returnRecord.containerYardName || "",
            containerYardAddress: returnRecord.containerYardAddress || returnRecord.containerYardLocation || booking.emptyReturnContainerYardAddress || booking.emptyReturnContainerYardLocation || "",
            arrivalTime: returnRecord.emptyReturnArrivedAt || returnRecord.arrivalDateTime || "",
            completionTime: returnRecord.emptyReturnCompletedAt || returnRecord.acceptanceDate || "",
            status,
            lastFreeDay: detentionInfo.lastFreeDay,
            detentionStartDate: detentionInfo.detentionStartDate,
            detentionStatus: detentionInfo.detentionStatus,
            daysRemaining: detentionInfo.daysRemaining,
            overdueDays: detentionInfo.overdueDays,
            detentionPenalty: detentionInfo.detentionPenalty,
            detentionRemarks: detentionInfo.detentionRemarks,
            rejectionReason,
            remarks: returnRecord.emptyReturnRemarks || returnRecord.containerYardRemarks || "",
            yardReceiverName: returnRecord.yardReceiverName || "",
            yardReceiverPosition: returnRecord.yardReceiverPosition || "",
            eirReceiptUrl,
            photoRequirementsVersion: returnRecord.photoRequirementsVersion || 1,
            photos,
            arrivalLat: getGpsValue(arrivalGps, "lat"),
            arrivalLng: getGpsValue(arrivalGps, "lng"),
            gpsAccuracy: arrivalGps && arrivalGps.accuracy,
            gpsCapturedAt: arrivalGps && arrivalGps.capturedAt,
            completionLat: getGpsValue(completionGps, "lat"),
            completionLng: getGpsValue(completionGps, "lng"),
            gpsAvailable: hasGps(returnRecord),
            geofenceChecked: returnRecord.emptyReturnGeofenceChecked === true,
            geofencePassed: returnRecord.emptyReturnGeofencePassed,
            geofenceDistanceMeters: returnRecord.emptyReturnGeofenceDistanceMeters,
            geofenceRadiusMeters: returnRecord.emptyReturnGeofenceRadiusMeters,
            yardLatitude: returnRecord.emptyReturnYardLatitude,
            yardLongitude: returnRecord.emptyReturnYardLongitude,
            proofAvailable: photos.some((item) => !!item.url),
            requiredPhotoCount: requiredPhotos.length,
            uploadedRequiredPhotoCount,
            photoChecklistComplete: uploadedRequiredPhotoCount === requiredPhotos.length,
            eirAvailable: !!eirReceiptUrl,
            arrivalPhotoUrl,
            proof,
            proofHash: proof.proofHash,
            timestamp: proof.timestampIso,
            notification: returnRecord.notification || null,
            sortTime: returnRecord.createdAt || proof.timestamp || 0
          };
        });
      })
      .sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0));
  }, [bookinglistdata.bookings, role, uid]);

  const yesNo = (value) => value ? label("true", "True") : label("false", "False");
  const emptyValue = label("not_available", "N/A");
  const detentionDaysSummary = (row) => {
    if (row.detentionStatus === "Overdue") return row.overdueDays === 0 || row.overdueDays ? `${row.overdueDays} ${label("overdue_days", "Overdue Days")}` : emptyValue;
    if (row.daysRemaining === 0 || row.daysRemaining) return `${row.daysRemaining} ${label("days_remaining", "Days Remaining")}`;
    return emptyValue;
  };
  const printBool = (value) => {
    if (value === true) return label("true", "True");
    if (value === false) return label("false", "False");
    return "";
  };
  const printMetric = (value) => value === 0 || value ? value : "";
  const printSelectedProof = () => {
    if (!selectedRow) return;
    openEmptyContainerReturnProofPrint({
      title: label("garahex_empty_container_return_proof", "Garahex Empty Container Return Proof"),
      emptyValue,
      generatedLabel: label("generated_date_time", "Generated Date/Time"),
      attachmentsTitle: label("proof_attachments", "Proof Attachments"),
      footer: label("proof_summary_footer", "This proof summary was generated from Garahex system records."),
      sections: [
        {
          title: label("booking_information", "Booking Information"),
          rows: [
            { label: label("booking_ref", "Booking Reference"), value: selectedRow.bookingReference },
            { label: label("booking_id", "Booking ID"), value: selectedRow.bookingId }
          ]
        },
        {
          title: label("booking_parties", "Booking / Parties"),
          rows: [
            { label: label("customer_name", "Customer Name"), value: selectedRow.customerName },
            { label: label("consigneeName", "Consignee Name"), value: selectedRow.consigneeName },
            { label: label("fleetadmin_name", "Fleet Admin / Logistics Company Name"), value: selectedRow.fleetAdminName },
            { label: label("driver_name", "Driver Name"), value: selectedRow.driverName }
          ]
        },
        {
          title: label("container_details", "Container Details"),
          rows: [
            { label: label("containerNumber", "Container Number"), value: selectedRow.containerNumber },
            { label: label("additional_container_numbers", "Additional Container Numbers"), value: selectedRow.additionalContainerNumbers },
            { label: label("shippingLine", "Shipping Line"), value: selectedRow.shippingLine },
            { label: label("container_yard_name", "Container Yard Name"), value: selectedRow.containerYardName },
            { label: label("container_yard_address", "Container Yard Address"), value: selectedRow.containerYardAddress }
          ]
        },
        {
          title: label("return_status", "Return Status"),
          rows: [
            { label: label("container_yard_status", "Status"), value: selectedRow.status },
            { label: label("container_yard_rejection_reason", "Rejection Reason"), value: selectedRow.rejectionReason },
            { label: label("container_yard_remarks", "Remarks"), value: selectedRow.remarks },
            { label: label("yard_receiver_name", "Yard Receiver Name"), value: selectedRow.yardReceiverName },
            { label: label("yard_receiver_position", "Yard Receiver Position"), value: selectedRow.yardReceiverPosition },
            { label: label("container_yard_arrival_datetime", "Arrival Time"), value: formatDateTime(selectedRow.arrivalTime) },
            { label: label("empty_return_completed_at", "Completion Time"), value: formatDateTime(selectedRow.completionTime) }
          ]
        },
        {
          title: label("gps_geofence_proof", "GPS / Geofence Proof"),
          rows: [
            { label: label("driver_latitude", "Driver Latitude"), value: selectedRow.arrivalLat },
            { label: label("driver_longitude", "Driver Longitude"), value: selectedRow.arrivalLng },
            { label: label("gps_accuracy", "GPS Accuracy"), value: formatMeters(selectedRow.gpsAccuracy) },
            { label: label("gps_captured_at", "GPS Captured At"), value: formatDateTime(selectedRow.gpsCapturedAt) },
            { label: label("geofence_checked", "Geofence Checked"), value: printBool(selectedRow.geofenceChecked) },
            { label: label("geofence_passed", "Geofence Passed"), value: selectedRow.geofenceChecked ? printBool(selectedRow.geofencePassed) : "" },
            { label: label("geofence_distance_meters", "Distance From Yard"), value: formatMeters(selectedRow.geofenceDistanceMeters) },
            { label: label("geofence_radius_meters", "Geofence Radius"), value: formatMeters(selectedRow.geofenceRadiusMeters) },
            { label: label("yard_latitude", "Yard Latitude"), value: selectedRow.yardLatitude },
            { label: label("yard_longitude", "Yard Longitude"), value: selectedRow.yardLongitude }
          ]
        },
        {
          title: label("detention_free_time", "Detention / Free Time"),
          rows: [
            { label: label("lastFreeDay", "Last Free Day"), value: formatDateTime(selectedRow.lastFreeDay) },
            { label: label("detentionStartDate", "Detention Start Date"), value: formatDateTime(selectedRow.detentionStartDate) },
            { label: label("detention_status", "Detention Status"), value: selectedRow.detentionStatus },
            { label: label("days_remaining", "Days Remaining"), value: printMetric(selectedRow.daysRemaining) },
            { label: label("overdue_days", "Overdue Days"), value: printMetric(selectedRow.overdueDays) },
            { label: label("detentionPenalty", "Estimated Penalty"), value: selectedRow.detentionPenalty },
            { label: label("detentionRemarks", "Detention Remarks"), value: selectedRow.detentionRemarks }
          ]
        },
        {
          title: label("notification", "Notification"),
          rows: [
            { label: label("email_sent", "Email Sent"), value: selectedRow.notification && selectedRow.notification.emailSent ? label("true", "True") : "" },
            { label: label("email_sent_at", "Email Sent At"), value: selectedRow.notification && selectedRow.notification.sentAt ? formatDateTime(selectedRow.notification.sentAt) : "" }
          ]
        }
      ],
      attachments: [
        ...selectedRow.photos.map((item) => ({
          label: label(item.labelKey, item.label),
          url: item.url
        })),
        { label: label("eir_yard_receipt", "EIR / Yard Receipt"), url: selectedRow.eirReceiptUrl }
      ]
    });
  };

  const columns = [
    { key: "details", label: label("details", "Details"), minWidth: 120, render: (row) => <ActionButton size="small" onClick={() => setSelectedRow(row)}>{label("view", "View")}</ActionButton> },
    { key: "bookingReference", label: label("booking_ref", "Booking Reference"), minWidth: 170 },
    { key: "driverName", label: label("driver_name", "Driver Name"), minWidth: 170 },
    { key: "customerConsignee", label: label("customer_consignee", "Customer / Consignee"), minWidth: 220, render: (row) => [row.customerName, row.consigneeName].filter(Boolean).join(" / ") },
    { key: "fleetAdminName", label: label("fleetadmin_name", "Fleet Admin / Logistics Company Name"), minWidth: 180 },
    { key: "containerNumber", label: label("containerNumber", "Container Number"), minWidth: 180 },
    { key: "containerYardName", label: label("container_yard_name", "Container Yard"), minWidth: 180 },
    { key: "arrivalTime", label: label("container_yard_arrival_datetime", "Arrival Time"), minWidth: 190, render: (row) => formatDateTime(row.arrivalTime) },
    { key: "completionTime", label: label("empty_return_completed_at", "Completion Time"), minWidth: 190, render: (row) => formatDateTime(row.completionTime) },
    { key: "status", label: label("container_yard_status", "Status"), minWidth: 130, render: (row) => <AdminStatusBadge status={row.status} /> },
    { key: "lastFreeDay", label: label("lastFreeDay", "Last Free Day"), minWidth: 180, render: (row) => formatDateTime(row.lastFreeDay) },
    { key: "detentionStatus", label: label("detention_status", "Detention Status"), minWidth: 170, render: (row) => row.detentionStatus ? <AdminStatusBadge status={row.detentionStatus} /> : emptyValue },
    { key: "detentionDaysSummary", label: label("days_remaining_overdue_days", "Days Remaining / Overdue Days"), minWidth: 220, render: detentionDaysSummary },
    { key: "rejectionReason", label: label("container_yard_rejection_reason", "Rejection Reason"), minWidth: 220 },
    { key: "remarks", label: label("container_yard_remarks", "Remarks"), minWidth: 220 },
    { key: "yardReceiverName", label: label("receiver", "Receiver"), minWidth: 170 },
    { key: "eirAvailable", label: label("eir_available", "EIR Available"), minWidth: 140, render: (row) => <AdminStatusBadge status={row.eirAvailable ? "AVAILABLE" : "PENDING"}>{yesNo(row.eirAvailable)}</AdminStatusBadge> },
    { key: "gpsAvailable", label: label("gps_available", "GPS Available"), minWidth: 140, render: (row) => <AdminStatusBadge status={row.gpsAvailable ? "AVAILABLE" : "PENDING"}>{yesNo(row.gpsAvailable)}</AdminStatusBadge> },
    { key: "geofencePassed", label: label("geofence_passed", "Geofence Passed"), minWidth: 150, render: (row) => row.geofenceChecked ? <AdminStatusBadge status={row.geofencePassed ? "APPROVED" : "REJECTED"}>{yesNoNa(row.geofencePassed, yesNo, emptyValue)}</AdminStatusBadge> : emptyValue },
    { key: "photoChecklist", label: label("photo_checklist", "Photo Checklist"), minWidth: 150, render: (row) => <StatusBadge tone={row.photoChecklistComplete ? "success" : "warning"}>{`${row.uploadedRequiredPhotoCount}/${row.requiredPhotoCount}`}</StatusBadge> },
    { key: "proofAvailable", label: label("proof_available", "Proof Available"), minWidth: 140, render: (row) => <AdminStatusBadge status={row.proofAvailable ? "AVAILABLE" : "PENDING"}>{yesNo(row.proofAvailable)}</AdminStatusBadge> }
  ];

  const detailRows = selectedRow ? [
    { label: label("booking_id", "Booking ID"), value: selectedRow.bookingId },
    { label: label("booking_ref", "Booking Reference"), value: selectedRow.bookingReference },
    { label: label("driver_name", "Driver Name"), value: selectedRow.driverName },
    { label: label("customer_name", "Customer Name"), value: selectedRow.customerName },
    { label: label("consigneeName", "Consignee Name"), value: selectedRow.consigneeName },
    { label: label("fleetadmin_name", "Fleet Admin / Logistics Company Name"), value: selectedRow.fleetAdminName },
    { label: label("containerNumber", "Container Number"), value: selectedRow.containerNumber },
    { label: label("additional_container_numbers", "Additional Container Numbers"), value: selectedRow.additionalContainerNumbers },
    { label: label("shippingLine", "Shipping Line"), value: selectedRow.shippingLine },
    { label: label("container_yard_name", "Container Yard Name"), value: selectedRow.containerYardName },
    { label: label("container_yard_address", "Container Yard Address"), value: selectedRow.containerYardAddress },
    { label: label("container_yard_arrival_datetime", "Arrival Time"), value: formatDateTime(selectedRow.arrivalTime) },
    { label: label("empty_return_completed_at", "Completion Time"), value: formatDateTime(selectedRow.completionTime) },
    { label: label("container_yard_status", "Status"), value: selectedRow.status },
    { label: label("lastFreeDay", "Last Free Day"), value: formatDateTime(selectedRow.lastFreeDay) },
    { label: label("detentionStartDate", "Detention Start Date"), value: formatDateTime(selectedRow.detentionStartDate) },
    { label: label("detention_status", "Detention Status"), value: selectedRow.detentionStatus },
    { label: label("days_remaining", "Days Remaining"), value: selectedRow.daysRemaining === 0 || selectedRow.daysRemaining ? selectedRow.daysRemaining : "" },
    { label: label("overdue_days", "Overdue Days"), value: selectedRow.overdueDays === 0 || selectedRow.overdueDays ? selectedRow.overdueDays : "" },
    { label: label("detentionPenalty", "Estimated Detention Penalty"), value: selectedRow.detentionPenalty },
    { label: label("detentionRemarks", "Detention Remarks"), value: selectedRow.detentionRemarks },
    { label: label("container_yard_rejection_reason", "Rejection Reason"), value: selectedRow.rejectionReason },
    { label: label("container_yard_remarks", "Remarks"), value: selectedRow.remarks },
    { label: label("yard_receiver_name", "Yard Receiver Name"), value: selectedRow.yardReceiverName },
    { label: label("yard_receiver_position", "Yard Receiver Position"), value: selectedRow.yardReceiverPosition },
    { label: label("eir_yard_receipt", "EIR / Yard Receipt"), value: selectedRow.eirReceiptUrl },
    { label: label("photo_requirements_version", "Photo Requirements Version"), value: selectedRow.photoRequirementsVersion },
    { label: label("photo_checklist", "Photo Checklist"), value: `${selectedRow.uploadedRequiredPhotoCount}/${selectedRow.requiredPhotoCount}` },
    { label: label("photo_checklist_complete", "Photo Checklist Complete"), value: yesNo(selectedRow.photoChecklistComplete) },
    { label: label("latitude", "Latitude"), value: selectedRow.arrivalLat },
    { label: label("longitude", "Longitude"), value: selectedRow.arrivalLng },
    { label: label("gps_accuracy", "GPS Accuracy"), value: formatMeters(selectedRow.gpsAccuracy) },
    { label: label("gps_captured_at", "GPS Captured At"), value: formatDateTime(selectedRow.gpsCapturedAt) },
    { label: label("completion_latitude", "Completion Latitude"), value: selectedRow.completionLat },
    { label: label("completion_longitude", "Completion Longitude"), value: selectedRow.completionLng },
    { label: label("geofence_checked", "Geofence Checked"), value: yesNo(selectedRow.geofenceChecked) },
    { label: label("geofence_passed", "Geofence Passed"), value: selectedRow.geofenceChecked ? yesNoNa(selectedRow.geofencePassed, yesNo, emptyValue) : emptyValue },
    { label: label("geofence_distance_meters", "Distance from Yard"), value: formatMeters(selectedRow.geofenceDistanceMeters) },
    { label: label("geofence_radius_meters", "Radius Used"), value: formatMeters(selectedRow.geofenceRadiusMeters) },
    { label: label("yard_latitude", "Yard Latitude"), value: selectedRow.yardLatitude },
    { label: label("yard_longitude", "Yard Longitude"), value: selectedRow.yardLongitude },
    { label: label("time_date_stamp", "Time / Date Stamp"), value: selectedRow.timestamp },
    { label: label("email_sent", "Email Sent"), value: selectedRow.notification && selectedRow.notification.emailSent ? yesNo(true) : "" },
    { label: label("email_sent_at", "Email Sent At"), value: selectedRow.notification && selectedRow.notification.sentAt ? formatDateTime(selectedRow.notification.sentAt) : "" },
    ...(isAdmin ? [{ label: label("proof_hash", "Proof Hash"), value: selectedRow.proofHash }] : [])
  ] : [];

  const renderFileLink = (title, url) => url ? (
    <Box style={{ marginTop: 12 }}>
      <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", marginBottom: 6 }}>{title}</Typography>
      <Button component="a" href={url} target="_blank" rel="noreferrer" variant="outlined" size="small">
        {label("view", "View")}
      </Button>
    </Box>
  ) : null;

  const renderProofImage = (title, url, alt, required, proof) => (
    <Box key={alt} style={{ border: `1px solid ${adminTokens.colors.border}`, borderRadius: 20, padding: 12, minHeight: 250, background: "linear-gradient(180deg, #FFFFFF, #F7FBFF)", boxShadow: "0 12px 30px rgba(3, 29, 54, 0.08)" }}>
      <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
          {title}
        </Typography>
        <StatusBadge tone={required ? "warning" : "info"}>
          {required ? label("required", "Required") : label("conditional", "Conditional")}
        </StatusBadge>
      </Box>
      {url
        ? imageCell(url, proof, alt)
        : <Typography style={{ color: required ? "#B42318" : "#6B7280", fontFamily: FONT_FAMILY, fontSize: 13 }}>
            {required ? label("required_photo_missing", "Required photo missing") : label("photo_not_supplied", "Photo not supplied")}
          </Typography>}
    </Box>
  );

  if (bookinglistdata.error && bookinglistdata.error.flag) {
    return <BookingDataLoadError message={bookinglistdata.error.msg} />;
  }

  if (bookinglistdata.loading) {
    return <CircularLoading />;
  }

  const summaryCards = [
    {
      label: label("empty_container_returns", "Empty Container Returns"),
      value: rows.length,
      tone: "info"
    },
    {
      label: label("proof_available", "Proof Available"),
      value: rows.filter((row) => row.proofAvailable).length,
      tone: "success"
    },
    {
      label: label("photo_checklist_complete", "Photo Checklist Complete"),
      value: rows.filter((row) => row.photoChecklistComplete).length,
      tone: "purple"
    },
    {
      label: label("geofence_passed", "Geofence Passed"),
      value: rows.filter((row) => row.geofenceChecked && row.geofencePassed).length,
      tone: "warning"
    }
  ];

  return (
    <Box className="garahex-operations-ecr-page">
      <PageHeader
        eyebrow={label("operations", "Operations")}
        title={label("empty_container_return", "Empty Container Return")}
        description={label("empty_container_return_operations_description", "Review container-return submissions, proof images, GPS evidence, EIR receipts, and current return status.")}
        actions={<StatusBadge tone="info">{`${rows.length} ${label("records", "records")}`}</StatusBadge>}
      />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {summaryCards.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <SurfaceCard sx={{ minHeight: 118 }}>
              <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{item.label}</Typography>
              <Typography sx={{ color: adminTokens.colors.text, fontSize: "1.9rem", fontWeight: 950, mt: 0.8 }}>{item.value}</Typography>
              <Box sx={{ mt: 1 }}><StatusBadge tone={item.tone}>{item.tone}</StatusBadge></Box>
            </SurfaceCard>
          </Grid>
        ))}
      </Grid>
      <SurfaceCard component="section" aria-labelledby="ecr-photo-requirements" sx={{ mb: 2 }}>
        <Typography id="ecr-photo-requirements" component="h2" sx={{ fontFamily: FONT_FAMILY, fontSize: 17, fontWeight: 700, color: "#102E56" }}>
          {label("ecr_current_photo_requirements", "Current Photo Requirements")}
        </Typography>
        <Typography sx={{ mt: 0.5, fontFamily: FONT_FAMILY, fontSize: 13, color: "#334968" }}>
          Ten required photos. Damage Photo is additionally required if rejected or damaged. EIR / Yard Receipt remains a separate required attachment.
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }, columnGap: 4, rowGap: 1 }}>
          {EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS.map((item) => (
            <Box component="li" key={item.key} sx={{ fontFamily: FONT_FAMILY, fontSize: 13, color: "#102E56", pr: 1 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>{label(item.labelKey, item.label)}</Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, color: "#475B73" }}>
                {item.required ? "Required" : "Required if rejected/damaged"}
              </Box>
            </Box>
          ))}
        </Box>
      </SurfaceCard>
      <SurfaceCard>
        <SectionHeader
          title={label("empty_container_returns_review", "Return Submissions Review")}
          description={label("empty_container_returns_review_description", "Existing rows, proof links, print proof action, and evidence details are preserved.")}
        />
      <Box style={{ width: "100%", overflowX: "auto", border: `1px solid ${adminTokens.colors.border}`, borderRadius: 20 }}>
        <Box component="table" style={{ minWidth: 3350, width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
          <Box component="thead">
            <Box component="tr">
              {columns.map((column) => (
                <Box
                  component="th"
                  key={column.key}
                  style={{
                    minWidth: column.minWidth,
                    backgroundColor: "#F7FBFF",
                    color: adminTokens.colors.deepNavy,
                    border: `1px solid ${adminTokens.colors.border}`,
                    padding: 10,
                    fontSize: 13,
                    textAlign: "left",
                    whiteSpace: "normal"
                  }}
                >
                  {column.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.length === 0 ? (
              <Box component="tr">
                <Box component="td" colSpan={columns.length} style={{ padding: 24, border: `1px solid ${adminTokens.colors.border}` }}>
                  <EmptyState title={label("no_operational_records", "No operational records")} description={label("no_ecr_records_found", "No empty-container-return submissions are available in the current booking scope.")} />
                </Box>
              </Box>
            ) : rows.map((row) => (
              <Box component="tr" key={row.id}>
                {columns.map((column) => (
                  <Box
                    component="td"
                    key={`${row.id}-${column.key}`}
                    style={{
                      minWidth: column.minWidth,
                      border: `1px solid ${adminTokens.colors.border}`,
                      padding: 10,
                      fontSize: 13,
                      verticalAlign: "top",
                      wordBreak: "break-word"
                    }}
                  >
                    {column.render ? column.render(row) : (row[column.key] || emptyValue)}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      </SurfaceCard>
      <Dialog open={!!selectedRow} onClose={() => setSelectedRow(null)} fullWidth maxWidth="md">
        <DialogTitle>{label("empty_container_return_details", "Empty Container Return Details")}</DialogTitle>
        <DialogContent dividers>
          {selectedRow ? (
            <>
              <Box component="table" style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
                <Box component="tbody">
                  {detailRows.map((row) => (
                    <Box component="tr" key={row.label}>
                      <Box component="th" style={{ width: "36%", border: `1px solid ${adminTokens.colors.border}`, padding: 8, textAlign: "left", backgroundColor: "#F7FBFF", color: adminTokens.colors.deepNavy }}>
                        {row.label}
                      </Box>
                      <Box component="td" style={{ border: `1px solid ${adminTokens.colors.border}`, padding: 8, wordBreak: row.label === label("proof_hash", "Proof Hash") ? "break-all" : "break-word" }}>
                        {row.value || emptyValue}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
              {renderFileLink(label("eir_yard_receipt", "EIR / Yard Receipt"), selectedRow.eirReceiptUrl)}
              <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", marginTop: 16, marginBottom: 8 }}>
                {label("required_proof_photos", "Required Proof Photos")}
              </Typography>
              <Box style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 12 }}>
                {selectedRow.photos.map((item) => renderProofImage(
                  label(item.labelKey, item.label), item.url, item.key, item.required, item.proof
                ))}
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <ActionButton onClick={printSelectedProof}>{label("print_ecr_proof", "Print ECR Proof")}</ActionButton>
          <Button onClick={() => setSelectedRow(null)}>{label("close", "Close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
